@import 'apps/webcomponents/src/assets/common.scss';
@import 'apps/webcomponents/src/assets/scss/mixins';

$listMaxHeight: 340px;

:host {
  --font-family: var(--font-family-secondary);
}


.dropdown {
  color: var(--body-text-arrows-400);
  font-size: var(--body-size);
  display: inline-block;
  width: var(--form-field-input-width, 200px);
  position: relative;
  transition: all 0.2s ease;

  &:hover {
    .dropdown-field {
      border-color: var(--body-text-arrows-400);
    }
  }

  &.open {
    .dropdown-list {
      display: block;
      border-color: var(--blue-layout-301-active);
    }

    .dropdown-field {
      border-color: var(--blue-layout-301-active);
    }
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  &.is-combobox {
    .dropdown-value {
      width: calc(100% - 48px);
    }
  }

  &.top {
    .dropdown-list {
      top: auto;
      bottom: calc(var(--spacer) * 4.5);
    }
  }

  &.borderless {
    width: inherit;

    .dropdown-field {
      border: none;
    }
    &.top {
      .dropdown-list {
        bottom: calc(var(--spacer) * 4);
      }
    }
  }
}

.dropdown-field {
  height: calc(var(--spacer) * 4);
  border-radius: var(--input-border-radius);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  position: relative;
  border-top: var(--dropdown-border-top, 1px solid var(--chart-100-pressed));
  border-bottom: var(--dropdown-border-bottom, 1px solid var(--chart-100-pressed));
  border-left: var(--dropdown-border-left, 1px solid var(--chart-100-pressed));
  border-right: var(--dropdown-border-right, 1px solid var(--chart-100-pressed));
  font-family: var(--font-family-secondary);
  background: var(--white-layout-301-level-0-elevation);

  &.floating-label .dropdown-label {
    @include floating-label(-70%);
    top: 2px !important;
    display: flex;
    align-items: center;
  }

  &.invalid {
    border-color: var(--red-layout-300-primary);
  }

  &:focus-within > .dropdown-label {
    color: var(--blue-layout-301-active);
  }
}

.dropdown-label {
  margin-left: var(--input-padding-left);
  transition: var(--floating-label-transition);
  font-family: var(--font-family-secondary);
}

.dropdown-value {
  display: block;
  padding: var(--input-padding);
  flex: 1;
  height: 100%;
  width: 100%;
  outline: none;
  border: none;
  box-shadow: none;
  color: var(--body-text-arrows-400);
  background: var(--white-layout-301-level-0-elevation);
  cursor: text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: text;
  border-bottom-left-radius: var(--input-border-radius-value, var(--input-border-radius));
  border-top-left-radius: var(--input-border-radius-value, var(--input-border-radius));
  font-size: var(--body-size);
  font-family: var(--font-family-secondary);
}

::placeholder {
  font-style: italic;
  color: var(--chart-300-icon-buttons);
  font-family: var(--font-family-secondary);
}

// visually hide text highlight for readonly input
.dropdown-value[readonly] {
  cursor: pointer;

  &::selection {
    color: none;
    background: none;
  }
}

:host([readonly]) {
  .dropdown {
    .dropdown-field {
      pointer-events: none;
      background: var(--chart-050-hover);
      border-color: transparent;

      .dropdown-label {
        &:before {
          background: transparent;
        }
      }
    }
    .dropdown-value,
    .dropdown-button {
      background-color: transparent;
    }
    .dropdown-arrow {
      border-top-color: var(--grey-layout-200-disabled);
    }
  }
}

.dropdown-button {
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background-color: var(--white-layout-301-level-0-elevation);
  color: var(--chart-300-icon-buttons);
  margin: 0;
  transition: all 0.2s ease;

  &--arrow {
    width: calc(var(--spacer) * 4);
    border-top-right-radius: var(--input-border-radius);
    border-bottom-right-radius: var(--input-border-radius);

    &:hover {
      background-color: var(--chart-050-hover);
    }

    &:active {
      background-color: var(--chart-102-pressed);
      .dropdown-arrow {
        border-top-color: var(--chart-300-icon-buttons);
      }
    }
  }

  &--delete {
    width: calc(var(--spacer) * 3);
    background-color: var(--white-layout-301-level-0-elevation);
    text-align: center;
    display: none;

    &:hover {
      gz-icon {
        background-color: var(--chart-050-hover);
      }
      gz-icon::part(svgIcon) {
        fill: var(--chart-300-icon-buttons);
      }
    }

    &:active {
      gz-icon {
        background-color: transparent;
      }
      gz-icon::part(svgIcon) {
        fill: var(--main-titles-inputs-600);
      }
    }

    &.visible {
      display: inline-flex;
    }
  }
}

.dropdown-arrow {
  display: inline-block;
  vertical-align: middle;
  position: absolute;
  top: calc(50% - 2px);
  width: 10px;
  height: 10px;
  border-top: solid 5px var(--chart-302-icon-buttons-titles);
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
}

.dropdown-list {
  display: none;
  min-width: var(--form-field-input-width, 200px);
  max-height: var(--gz-dropdown-max-height, $listMaxHeight);
  overflow: hidden;
  width: 100%;
  position: absolute;
  z-index: 10000;
  top: calc(var(--spacer) * 5);
  border-radius: var(--input-border-radius);
  border: 1px solid var(--chart-101-pressed-dividers);
  background-color: var(--white-layout-301-level-0-elevation);

  &--inner {
    max-height: var(--gz-dropdown-max-height, $listMaxHeight);
    width: 100%;
    overflow: auto;
  }

  &--option {
    cursor: pointer;
    height: calc(var(--spacer) * 4);
    transition: all 0.2s ease;
    background-color: var(--white-layout-301-level-0-elevation);
    pointer-events: initial;
    padding: var(--spacer) calc(var(--spacer) * 2) var(--spacer) var(--spacer);
    position: relative;
    display: flex;
    justify-content: start;
    align-items: flex-start;
    color: var(--main-titles-inputs-600);
    font-size: var(--body-size-large);

    &.disabled {
      pointer-events: none;
    }

    &.no-results {
      padding-top: calc(3 * var(--spacer));
      padding-bottom: calc(3 * var(--spacer));
      align-items: center;
    }

    &.error {
      display: flex;
      padding-top: calc(3 * var(--spacer));
      padding-bottom: calc(3 * var(--spacer));
      height: initial;

      .icon {
        margin-right: calc(var(--spacer) / 2);
        margin-top: 2px;
      }
    }

    &.active {
      background-color: var(--blue-layout-050-selected) !important;
    }

    &.focused {
      background-color: var(--blue-layout-050-selected);
    }

    &:hover {
      background-color: var(--chart-051-hover);
    }

    &:active {
      background-color: var(--blue-layout-050-selected);
    }

    .tooltip {
      position: relative;
      width: auto;
      height: auto;
    }
  }
}

.spinner {
  justify-content: center;

  gz-progress-spinner {
    width: fit-content;
    display: flex;
    transform: scale(1.5);
  }
}

:host([isLoading]) {
  .dropdown-list {
    &--inner {
      overflow: hidden;
    }
  }
}

:host([isError]) {
  .dropdown {
    .dropdown-field,
    .dropdown-list {
      border-color: var(--red-layout-300-primary);
      background: var(--white-layout-301-level-0-elevation);
    }

    .dropdown-field > .dropdown-label {
      color: var(--red-layout-300-primary);
      background: var(--white-layout-301-level-0-elevation);
    }

    .dropdown-list {
      &--option {
        padding: var(--spacer);
        color: var(--red-layout-300-primary);
        line-height: var(--body-size);
      }
    }
  }
}

@keyframes fadeInBottom {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

$labelWidths: 80, 160, 240;
$labelWidthFull: 100%;

@each $size in $labelWidths {
  :host([labelWidth='#{$size}']) {
    .dropdown {
      width: #{$size}px;
    }
  }
}

:host([labelWidth='full']) {
  .dropdown {
    width: $labelWidthFull;
  }
}
